/**
 * SliderRangeComponent
 *
 * Numeric range slider for form inputs
 *
 * Features:
 * - Configurable min/max
 * - Step size
 * - Visual marks/ticks
 * - Labels on marks
 * - Single or range selection
 */

import React, { useCallback, useMemo, memo } from 'react';
import type { FormControlProps } from '../../types';

interface Mark {
  value: number;
  label: string;
}

interface SliderRangeComponentProps extends FormControlProps {
  min?: number;
  max?: number;
  step?: number;
  marks?: Mark[];
  showLabels?: boolean;
  orientation?: 'horizontal' | 'vertical';
  isRange?: boolean;
  onChange?: (value: number | number[]) => void;
}

export const SliderRangeComponent: React.FC<SliderRangeComponentProps> = memo(({
  value = 50,
  min = 0,
  max = 100,
  step = 1,
  marks = [],
  showLabels = false,
  orientation = 'horizontal',
  isRange = false,
  onChange,
  disabled = false,
  required = false,
  label,
  error,
  helpText,
}) => {
  const inputId = useMemo(
    () => `slider-${label?.replace(/\s+/g, '-').toLowerCase()}`,
    [label],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.currentTarget.value);
      onChange?.(val);
    },
    [onChange],
  );

  const currentValue = Array.isArray(value) ? value[0] : (value as number);

  const markElements = marks.map((mark) => {
    const percentage = ((mark.value - min) / (max - min)) * 100;
    const isHorizontal = orientation === 'horizontal';

    return (
      <div
        key={`${mark.value}-${mark.label}`}
        className="slider-range__mark"
        style={
          isHorizontal
            ? { left: `${percentage}%` }
            : { bottom: `${percentage}%` }
        }
        role="presentation"
      >
        {showLabels && (
          <span className="slider-range__mark-label">{mark.label}</span>
        )}
      </div>
    );
  });

  return (
    <div className="af-form-field__group slider-range-group">
      {label && (
        <label htmlFor={inputId} className="af-form-field__label">
          {label}
          {required && <span className="af-form-field__required">*</span>}
        </label>
      )}

      <div className={`slider-range__container slider-range--${orientation}`}>
        <div className="slider-range__track" role="presentation">
          {markElements}
        </div>

        <input
          id={inputId}
          type="range"
          min={min}
          max={max}
          step={step}
          value={currentValue}
          onChange={handleChange}
          disabled={disabled}
          className="slider-range__input"
          aria-label={label || 'Range slider'}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : helpText ? `${inputId}-help` : undefined}
        />

        {showLabels && (
          <div className="slider-range__value-display">
            Value: {currentValue}
          </div>
        )}
      </div>

      {error && (
        <div id={`${inputId}-error`} className="af-form-field__error">
          {error}
        </div>
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-form-field__help">
          {helpText}
        </div>
      )}
    </div>
  );
});
